import type { CanvasNodeSource } from "@/analytics/surfaces";
import { useCanvasById } from "@/api/canvas";
import { SELF_CANVAS_LINK_REASON } from "@/dnd/classifyTransfer";
import { markInsertSource } from "@/pages/canvas/analytics/insertSource";
import type { QuickAddCursorState } from "@/pages/canvas/context/CanvasUiContext";
import type { WidgetPreset } from "@/pages/canvas/nodes/widgets";
import { createCascadePlacementState } from "@/pages/canvas/utils/canvasInsertCascadePlacement";
import { buildCanvasLinkEntity } from "@/pages/canvas/utils/canvasLinkEntity";
import type { Canvas, FileRecord } from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import { getEntityName } from "@cohesive/ui-components";
import { useCallback, useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { useCanvasFileIngest } from "./useCanvasFileIngest";
import { useCanvasWidgetQuickPlace } from "./useCanvasWidgetQuickPlace";
import { useCreateDocOnCanvas } from "./useCreateDocOnCanvas";
import type { DeferredQuickComposeMode } from "./useDeferredQuickChatPlacement";
import { useFileRecordCreateFromURL } from "./useFileRecordCreateFromURL";

/** Toolbar and left rail share this profile exactly. */
export type CanvasInsertPlacementProfile = "chrome" | "contextMenu";

export type CanvasInsertSource = "toolbar" | "rail" | "paneContextMenu";

export interface CanvasInsertMenuContext {
  source: CanvasInsertSource;
  placement: CanvasInsertPlacementProfile;
  canvasId: string | undefined;
  cursor?: QuickAddCursorState | null;
}

export interface CanvasInsertMenuActions {
  onAddWidget: (preset: WidgetPreset) => void;
  onAddLinkSubmit: (url: string) => void;
  onNewNote: () => void;
  onNewDocument: () => void;
  onUploadFile: () => void;
  onUploadFolder: () => void;
  /** Pick a workspace file and place it as a reference (A2). Absent while the
   *  canvas's workspace is unknown. */
  onAddFromLibrary?: {
    workspaceId: string;
    onPick: (record: FileRecord, at?: { x: number; y: number }) => void;
  };
  /** Pick a canvas and place it as a link card — reference-only, never a
   *  copy. Absent when no canvas hosts the menu. */
  onAddCanvas?: {
    /** The host canvas's workspace — the picker lists it first. */
    workspaceId: string | undefined;
    currentCanvasId: string;
    onPick: (canvas: Canvas) => void;
  };
  onSelectEntity: (
    entity: Entity,
    flowPosition: { x: number; y: number },
  ) => void;
  onStartQuickChat?: () => void;
  onStartQuickVoice?: () => void;
  onStartComment?: () => void;
  onPaste?: () => void;
}

export interface ChromeInsertHandlers {
  onNewNote: () => void;
  onNewDocument: () => void;
  onUploadFile: () => void;
  onUploadFolder: () => void;
  onAddLinkSubmit: (url: string) => void;
  onAddWidget: (preset: WidgetPreset) => void;
  onSelectEntityViewport: (entity: Entity) => void;
}

export interface UseCanvasInsertMenuActionsParams {
  context: CanvasInsertMenuContext;
  onClose?: () => void;
  chrome: ChromeInsertHandlers;
  startTextNotePlacement: (screenPos?: { x: number; y: number } | null) => void;
  onSelectEntityAtPosition: (
    entity: Entity,
    flowPosition: { x: number; y: number },
  ) => void;
  /** Opens the OS folder picker; the tree lands at `flowPosition`. */
  pickFolderAt: (flowPosition: { x: number; y: number }) => void;
  onPasteAtPosition?: (flowPosition: { x: number; y: number }) => void;
  onStartQuickChat?: (
    screenPosition: { x: number; y: number },
    initialContent?: string,
    source?: string,
  ) => void;
  onStartQuickVoice?: (
    screenPosition: { x: number; y: number },
    source?: string,
  ) => void;
  /** Chrome profile: pick chat/voice/comment, then click the canvas to place. */
  beginDeferredQuickCompose?: (mode: DeferredQuickComposeMode) => void;
  /** Context-menu profile: drop the comment draft straight at the cursor. */
  onStartCommentAtPosition?: (flowPosition: { x: number; y: number }) => void;
  /** Gated on `can_comment`; absent drops the Comment row. */
  canComment?: boolean;
}

export function useCanvasInsertMenuActions({
  context,
  onClose,
  chrome,
  startTextNotePlacement,
  onSelectEntityAtPosition,
  pickFolderAt,
  onPasteAtPosition,
  onStartQuickChat,
  onStartQuickVoice,
  beginDeferredQuickCompose,
  onStartCommentAtPosition,
  canComment,
}: UseCanvasInsertMenuActionsParams) {
  const { placement, source, canvasId, cursor } = context;
  const { data: hostCanvas } = useCanvasById(canvasId);
  const hostWorkspaceId = hostCanvas?.workspace_id;
  const { placeWidget } = useCanvasWidgetQuickPlace();
  const createDoc = useCreateDocOnCanvas();
  const ingestURL = useFileRecordCreateFromURL();
  const { ingestFiles } = useCanvasFileIngest(canvasId);

  const cascadeRef = useRef<ReturnType<
    typeof createCascadePlacementState
  > | null>(null);

  useEffect(() => {
    if (!cursor || placement !== "contextMenu") {
      cascadeRef.current = null;
      return;
    }
    cascadeRef.current = createCascadePlacementState(
      cursor.flowPosition,
      cursor.clientX,
    );
  }, [cursor, placement]);

  const getCascade = useCallback(() => {
    if (!cascadeRef.current || !cursor) {
      throw new Error("contextMenu cascade requires cursor");
    }
    return cascadeRef.current;
  }, [cursor]);

  const closeIfNeeded = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const nodeSource: CanvasNodeSource =
    source === "rail"
      ? "rail"
      : source === "toolbar"
        ? "toolbar"
        : "context_menu";

  const actions = useMemo((): CanvasInsertMenuActions => {
    // Shared by both placements: refuse the host canvas (it can't hold a
    // card of itself), then place the link entity the placement's way.
    const buildAddCanvas = (
      place: (entity: Entity) => void,
    ): CanvasInsertMenuActions["onAddCanvas"] =>
      canvasId
        ? {
            workspaceId: hostWorkspaceId,
            currentCanvasId: canvasId,
            onPick: (canvas) => {
              if (canvas.id === canvasId) {
                toast.error(SELF_CANVAS_LINK_REASON);
                return;
              }
              markInsertSource("canvas_picker");
              place(buildCanvasLinkEntity(canvas));
              toast.success(`Added ${canvas.name || "Untitled"} to canvas`, {
                duration: 2000,
                icon: "✓",
              });
              closeIfNeeded();
            },
          }
        : undefined;

    if (placement === "chrome") {
      return {
        onAddWidget: (preset) => {
          markInsertSource(nodeSource);
          chrome.onAddWidget(preset);
        },
        onAddLinkSubmit: (url) => {
          markInsertSource(nodeSource);
          chrome.onAddLinkSubmit(url);
        },
        onNewNote: () => {
          markInsertSource(nodeSource);
          chrome.onNewNote();
        },
        onNewDocument: () => {
          markInsertSource(nodeSource);
          chrome.onNewDocument();
        },
        onUploadFile: () => {
          markInsertSource(nodeSource);
          chrome.onUploadFile();
        },
        onUploadFolder: () => {
          markInsertSource(nodeSource);
          chrome.onUploadFolder();
        },
        onSelectEntity: (entity) => {
          markInsertSource(nodeSource);
          chrome.onSelectEntityViewport(entity);
        },
        onAddCanvas: buildAddCanvas((entity) =>
          chrome.onSelectEntityViewport(entity),
        ),
        onStartQuickChat:
          beginDeferredQuickCompose && onStartQuickChat
            ? () => {
                closeIfNeeded();
                beginDeferredQuickCompose("quick-chat");
              }
            : undefined,
        onStartQuickVoice:
          beginDeferredQuickCompose && onStartQuickVoice
            ? () => {
                closeIfNeeded();
                beginDeferredQuickCompose("quick-voice");
              }
            : undefined,
        onStartComment:
          beginDeferredQuickCompose && canComment
            ? () => {
                closeIfNeeded();
                beginDeferredQuickCompose("comment");
              }
            : undefined,
        onPaste: undefined,
      };
    }

    if (!cursor) {
      return {
        onAddWidget: () => {},
        onAddLinkSubmit: () => {},
        onNewNote: () => {},
        onNewDocument: () => {},
        onUploadFile: () => {},
        onUploadFolder: () => {},
        onSelectEntity: () => {},
      };
    }

    const cascade = getCascade;

    return {
      onAddWidget: (preset) => {
        markInsertSource(nodeSource);
        placeWidget(preset, cascade().snapCursor());
        closeIfNeeded();
      },
      onAddLinkSubmit: (url) => {
        markInsertSource(nodeSource);
        ingestURL({ sourceUrl: url, position: cascade().snapCursor() });
        closeIfNeeded();
      },
      onNewNote: () => {
        markInsertSource(nodeSource);
        startTextNotePlacement({ x: cursor.clientX, y: cursor.clientY });
        closeIfNeeded();
      },
      onNewDocument: () => {
        markInsertSource(nodeSource);
        createDoc(cascade().snapCursor());
        closeIfNeeded();
      },
      onUploadFile: () => {
        // File picker is opened by CanvasInsertMenu; ingest runs in ingestContextMenuFiles.
      },
      onUploadFolder: () => {
        markInsertSource(nodeSource);
        pickFolderAt(cascade().snapCursor());
        closeIfNeeded();
      },
      onSelectEntity: (entity, flowPosition) => {
        markInsertSource(nodeSource);
        onSelectEntityAtPosition(entity, flowPosition);
        toast.success(`Added ${getEntityName(entity)} to canvas`, {
          duration: 2000,
          icon: "✓",
        });
        closeIfNeeded();
      },
      onAddCanvas: buildAddCanvas((entity) =>
        onSelectEntityAtPosition(entity, cascade().snapCursor()),
      ),
      onStartQuickChat: onStartQuickChat
        ? () => {
            closeIfNeeded();
            onStartQuickChat(
              { x: cursor.clientX, y: cursor.clientY },
              undefined,
              nodeSource,
            );
          }
        : undefined,
      onStartQuickVoice: onStartQuickVoice
        ? () => {
            closeIfNeeded();
            onStartQuickVoice(
              { x: cursor.clientX, y: cursor.clientY },
              nodeSource,
            );
          }
        : undefined,
      onStartComment:
        onStartCommentAtPosition && canComment
          ? () => {
              closeIfNeeded();
              onStartCommentAtPosition(cursor.flowPosition);
            }
          : undefined,
      onPaste: onPasteAtPosition
        ? () => {
            onPasteAtPosition(cascade().snapCursor());
            closeIfNeeded();
          }
        : undefined,
    };
  }, [
    placement,
    source,
    nodeSource,
    cursor,
    canvasId,
    hostWorkspaceId,
    chrome,
    closeIfNeeded,
    onStartQuickChat,
    onStartQuickVoice,
    beginDeferredQuickCompose,
    onStartCommentAtPosition,
    canComment,
    getCascade,
    placeWidget,
    ingestURL,
    startTextNotePlacement,
    createDoc,
    onSelectEntityAtPosition,
    pickFolderAt,
    onPasteAtPosition,
  ]);

  const handleContextMenuEntitySelect = useCallback(
    (entity: Entity) => {
      if (placement !== "contextMenu" || !cursor) return;
      const cascade = getCascade();
      const flowPos = cascade.next();
      actions.onSelectEntity(entity, flowPos);
    },
    [placement, cursor, getCascade, actions],
  );

  const ingestContextMenuFiles = useCallback(
    async (files: File[]) => {
      if (placement !== "contextMenu") return;
      const cascade = getCascade();
      markInsertSource("context_menu");
      await ingestFiles(files, {
        placement: { strategy: "cascade", next: () => cascade.next() },
      });
      closeIfNeeded();
    },
    [placement, getCascade, ingestFiles, closeIfNeeded],
  );

  const handleContextMenuEntitySelectWithDims = useCallback(
    (entity: Entity) => {
      if (placement === "contextMenu") {
        handleContextMenuEntitySelect(entity);
        return;
      }
      markInsertSource(nodeSource);
      chrome.onSelectEntityViewport(entity);
      toast.success(`Added ${getEntityName(entity)} to canvas`, {
        duration: 2000,
        icon: "✓",
      });
    },
    [placement, handleContextMenuEntitySelect, chrome, nodeSource],
  );

  return {
    actions,
    ingestContextMenuFiles,
    handleIntegrationEntitySelect: handleContextMenuEntitySelectWithDims,
  };
}
